import { FC, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  Stack,
  Paper,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Event as EventIcon,
  ChevronRight as ChevronRightIcon,
  LocationOn as LocationIcon,
  Receipt as ReceiptIcon,
  MedicalServices as MedicalIcon,
  Person as PersonIcon,
  CalendarMonth as CalendarIcon,
} from '@mui/icons-material';
import HoofIcon from '../../components/HoofIcon';
import { useQuery } from 'react-query';
import { appointmentApi, horseApi, clientApi, billingApi, ClientStatus } from '../../utils/api';
import { isToday, addDays, isSameDay, startOfDay, endOfDay } from 'date-fns';
import { Link } from 'react-router-dom';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { useAuth } from '../../auth/useAuth';
import { useRBAC } from '../../auth/useRBAC';
import { dashboardControls, anyControl } from '../../auth/pageControls';
import { withOfflineFallback } from '../../offline/offlineQuery';
import {
  readAppointmentsInRange,
  readInvoiceStats,
  readHorseCount,
  readActiveClientsCount,
  OPEN_INVOICE_STATUSES,
} from '../../offline/readers';
import { formatDollars } from '../../utils/money';
import { isCancelled } from '../Appointments/appointmentStatus';

interface Owner {
  name: string;
  email?: string;
  phone?: string;
}

interface Horse {
  id: string;
  name: string;
  owners: Owner[];
  lastVaccination?: string;
}

interface Appointment {
  id: string;
  date: string;
  type: string;
  horseId: string;
  horse: Horse;
  location?: string;
  status?: string;
  assignedVet?: { name?: string; firstName?: string; lastName?: string };
}

const getTypeChipStyle = (type: string) => {
  const t = type.toLowerCase();
  if (t.includes('vaccin')) return { bgcolor: '#E3F2FD', color: '#1565C0' };
  if (t.includes('check') || t.includes('wellness')) return { bgcolor: '#E8F5E9', color: '#2E7D32' };
  if (t.includes('dental')) return { bgcolor: '#FFF3E0', color: '#E65100' };
  if (t.includes('emergency')) return { bgcolor: '#FFEBEE', color: '#C62828' };
  if (t.includes('surgery')) return { bgcolor: '#FCE4EC', color: '#AD1457' };
  return { bgcolor: '#F3E5F5', color: '#6A1B9A' };
};

const getStatusChipStyle = (date: string) => {
  if (new Date(date) < new Date()) {
    return { bgcolor: '#F5F5F5', color: '#9E9E9E', label: 'Past' };
  }
  return { bgcolor: '#E3F2FD', color: '#1565C0', label: 'Upcoming' };
};

const Dashboard: FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useAuth();
  const rbac = useRBAC();
  const { claims } = rbac;
  const formatInstant = useFormatInstant();
  /**
   * THE LANDING SCREEN, SO THE READS MATTER AS MUCH AS THE BUTTONS (INV-108).
   *
   * Dashboard is deliberately absent from `SCREEN_ACCESS` — it is always reachable — so every
   * tile and shortcut gates itself. A tile whose data the person cannot read is not rendered AND
   * its query never fires (`enabled:` below): without that, a narrowed person's every session
   * would open with four refused requests, and until A1's offline fix that pattern also served
   * them the local replica of data they had just been refused.
   */
  const can = dashboardControls(rbac);

  const getUserInitials = () => {
    if (user?.displayName) {
      return user.displayName
        .split(' ')
        .map((n: string) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (user?.email) return user.email[0].toUpperCase();
    return '?';
  };

  // Narrow the dashboard's working set. Previously this page fetched every
  // appointment, every invoice, and every horse in the clinic on mount,
  // which was a primary cause of the "show nothing with lots of data" hang.
  const now = useMemo(() => new Date(), []);
  const weekWindow = useMemo(
    () => ({
      from: startOfDay(now).toISOString(),
      to: endOfDay(addDays(now, 7)).toISOString(),
    }),
    [now],
  );

  // Appointments for today + next 7 days — bounded dataset used by the
  // "today's appointments" list and the upcoming-stats tile.
  const { data: appointments = [] } = useQuery<Appointment[]>(
    ['dashboard-appointments', weekWindow],
    withOfflineFallback(
      async () =>
        (await appointmentApi.list({
          dateFrom: weekWindow.from,
          dateTo: weekWindow.to,
        })) as unknown as Appointment[],
      // Offline replica returns Paginated<Row>; the UI consumes a plain
      // Appointment[], so unwrap `.data` to match the network shape exactly.
      async () =>
        (await readAppointmentsInRange({
          dateFrom: weekWindow.from,
          dateTo: weekWindow.to,
        })).data as unknown as Appointment[],
    ),
    { staleTime: 30_000, enabled: can.todaysAppointmentsTile },
  );

  // Unpaid-invoice tile — the CANONICAL clinic AR figure from
  // GET /billing/stats/clinic (billing.service.getClinicInvoiceStats): open
  // statuses INCLUDING partially_paid, succeeded+processing netted out per
  // invoice, past due derived from dueDate in the clinic's zone. It used to
  // fetch the WHOLE /billing list and sum gross amount+tax per STORED status in
  // JS — a number that disagreed with the Billing page, the AR report and the
  // client balances, on top of pulling every invoice into the browser.
  // Its own cache entry, NOT ['billing-stats']: the Billing page caches the
  // full ClinicInvoiceStats there, and two different shapes under one key would
  // poison whichever page mounted second. It stays under the 'invoices' PREFIX
  // (the same one the Billing list's ['invoices', {…}] key uses) because ~15
  // mutation sites call invalidateQueries(['invoices']) — record a payment,
  // void, bulk mark paid, the AI widget — and React Query v3 matches keys by
  // prefix. A key nothing invalidates would leave a MONEY figure stale for a
  // whole staleTime right after the payment that changed it, which is the
  // cross-page disagreement this task exists to remove.
  const { data: unpaid } = useQuery(
    ['invoices', 'dashboard-unpaid-stats'],
    withOfflineFallback(
      async () => {
        const s = await billingApi.getClinicStats();
        // `?? pendingAmount/overdueAmount` covers a response from before the
        // additive A5b keys existed — a warm React Query cache, or the normal
        // promotion window where this bundle and the API are separate Render
        // services. The keys are declared REQUIRED on `ClinicInvoiceStats`, so
        // TypeScript cannot see that case: without the fallback
        // `undefined + undefined` is NaN and the landing screen's money tile
        // reads "$NaN" beside a plausible "0 Open". `??`, never `||` — a
        // genuine 0 must stay 0 (the Billing page pins the same pair,
        // `Billing/index.tsx` stats memo + `headerTiles.test.tsx`).
        return {
          owed: (s.outstandingAmount ?? s.pendingAmount ?? 0) + (s.pastDueAmount ?? s.overdueAmount ?? 0),
          openCount: s.openInvoiceCount ?? ((s.pendingCount ?? 0) + (s.overdueCount ?? 0)),
          pastDueCount: s.overdueCount ?? 0,
        };
      },
      // Payments ARE replicated, so readInvoiceStats nets exactly the way the
      // server does and the tile keeps its value offline (INV-19). The one
      // thing the replica cannot reproduce is the PAST-DUE split (it needs the
      // clinic zone), so the whole open balance reads as owed and the past-due
      // caption is suppressed rather than guessed.
      // `owedCount`, not `count`: the server's openInvoiceCount is
      // COUNT(*) FILTER (WHERE owed > 0), so a fully-paid row that still
      // carries an open status is excluded on BOTH arms.
      async () => {
        const open = await readInvoiceStats([...OPEN_INVOICE_STATUSES]);
        return { owed: open.total, openCount: open.owedCount, pastDueCount: 0 };
      },
    ),
    { staleTime: 60_000, enabled: can.unpaidInvoicesTile },
  );

  // We only need the TOTAL patient count for the dashboard tile, not the
  // full horse list. Fetch one horse (limit=1) and read `total` from the
  // paginated envelope — constant-time regardless of clinic size.
  const { data: horsesCountData } = useQuery(
    ['dashboard-horse-count'],
    withOfflineFallback(
      () => horseApi.listPaginated({ page: 1, limit: 1 }),
      // UI only reads `.total`; map the replica count into the paginated
      // envelope so the consumed field matches the network shape.
      async () => ({ total: await readHorseCount() }) as any,
    ),
    { staleTime: 60_000, enabled: can.patientsTile },
  );
  const totalPatients = horsesCountData?.total ?? 0;

  // Same trick for active clients (replaces the old unique-owners
  // heuristic, which was both expensive and inaccurate).
  const { data: activeClientsData } = useQuery(
    ['dashboard-active-clients'],
    withOfflineFallback(
      () => clientApi.listPaginated({ page: 1, limit: 1, status: ClientStatus.ACTIVE }),
      // UI only reads `.total`; map the replica count into the paginated
      // envelope so the consumed field matches the network shape.
      async () => ({ total: await readActiveClientsCount() }) as any,
    ),
    { staleTime: 60_000, enabled: can.activeClientsTile },
  );
  const activeClientsCount = activeClientsData?.total ?? 0;

  // Note: the dashboard no longer holds the full horse list in memory.
  // Counts come from `totalPatients` / `activeClientsCount` above.

  const stats = useMemo(() => {
    // Cancel keeps the row now (ADR-0045); a cancelled visit is not one of today's.
    const todaysAppts = appointments.filter(app => isToday(new Date(app.date)) && !isCancelled(app.status));
    // Clinic = at the clinic. A barn visit is anything that goes out to the horse: a farm call
    // ('farm') or a barn booking, whose location is the barn's name. Compared like the Schedule does.
    const place = (app: Appointment) => app.location?.trim().toLowerCase();
    const inClinic = todaysAppts.filter(app => place(app) === 'clinic').length;
    const fieldVisits = todaysAppts.filter(app => !!place(app) && place(app) !== 'clinic').length;

    const unpaidInvoices = unpaid?.owed ?? 0;
    const openInvoicesCount = unpaid?.openCount ?? 0;

    return {
      todaysAppointments: todaysAppts.length,
      unpaidInvoices,
      openInvoicesCount,
      activeClients: activeClientsCount,
      totalPatients,
      fieldVisits,
      inClinic,
    };
  }, [appointments, unpaid, activeClientsCount, totalPatients]);

  // (Legacy alerts panel was already unused; removed along with the full-
  // horse-list fetch. Re-add as a dedicated `/alerts` endpoint if needed.)


  const todaysAppointments = useMemo(() => {
    return appointments
      .filter(app => isToday(new Date(app.date)))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .map(app => {
        // Guard the nested horse: offline, readAppointmentsInRange hydrates it
        // from the replica, but a horse missing from the replica leaves it
        // undefined — an unguarded deref would crash the dashboard list.
        const mainOwner = Array.isArray(app.horse?.owners) && app.horse.owners.length > 0
          ? app.horse.owners[0]
          : null;
        const vetName = app.assignedVet
          ? `Dr. ${app.assignedVet.lastName || app.assignedVet.firstName || app.assignedVet.name || 'Unknown'}`
          : undefined;
        return {
          id: app.id,
          date: app.date,
          // INSTANT (Appointment.date, DATE-KINDS §1.3) — the clinic's wall clock,
          // not the viewer's browser: a relief vet reading the board from another
          // zone must see the clinic's 9:00, and near midnight the two disagree
          // on the DAY, not just the hour.
          time: formatInstant(app.date, 'hh:mm'),
          period: formatInstant(app.date, 'a').toUpperCase(),
          horseName: app.horse?.name ?? 'Unknown',
          type: app.type,
          ownerName: mainOwner?.name || 'Unknown',
          location: app.location,
          status: app.status || 'scheduled',
          vetName,
        };
      });
  }, [appointments, formatInstant]);

  const upcomingStats = useMemo(() => {
    const now = new Date();
    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);

    const upcoming = appointments.filter(app => {
      const appDate = new Date(app.date);
      return appDate > now && appDate <= weekFromNow;
    });

    return {
      // Cancel keeps the row now, so "booked" leaves cancelled visits out.
      total: upcoming.filter(app => !isCancelled(app.status)).length,
      confirmed: upcoming.filter(
        app => !app.status || app.status === 'scheduled' || app.status === 'confirmed'
      ).length,
      cancelled: upcoming.filter(app => app.status === 'cancelled').length,
    };
  }, [appointments]);

  return (
    <Box sx={{ overflowX: 'hidden', maxWidth: 1280, mx: 'auto' }}>
      {/* Header */}
      <Box mb={3}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: '"Inter", sans-serif',
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2.125rem' },
            mb: 0.5,
          }}
        >
          Organization Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          {user?.displayName || 'Your clinic'}
        </Typography>

        {/* If every shortcut is hidden the ROW goes too: an empty strip of padding under the
            heading reads as a rendering fault, not as "nothing here for you". */}
        {anyControl(can.newAppointment, can.addHorse, can.newInvoice, can.addRecord) && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {can.newAppointment && (
          <Button
            component={Link}
            to="/appointments"
            variant="contained"
            startIcon={<CalendarIcon />}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              minHeight: 38,
              bgcolor: '#3B2E1A',
              color: '#fff',
              '&:hover': { bgcolor: '#2A2012' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: 'none',
              '&:active': { boxShadow: 'none' },
            }}
          >
            New Appt
          </Button>
          )}
          {can.addHorse && (
          <Button
            component={Link}
            to="/patients"
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              minHeight: 38,
              borderColor: '#E5E7EB',
              color: '#3B2E1A',
              '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            + Add Horse
          </Button>
          )}
          {can.newInvoice && (
          <Button
            component={Link}
            to="/billing"
            variant="outlined"
            startIcon={<ReceiptIcon />}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              minHeight: 38,
              borderColor: '#E5E7EB',
              color: '#3B2E1A',
              '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            New Invoice
          </Button>
          )}
          {can.addRecord && (
          <Button
            component={Link}
            to="/clinical-records"
            variant="outlined"
            startIcon={<MedicalIcon />}
            size={isMobile ? 'small' : 'medium'}
            sx={{
              minHeight: 38,
              borderColor: '#E5E7EB',
              color: '#3B2E1A',
              '&:hover': { borderColor: '#d1d5db', bgcolor: '#f9fafb' },
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500,
            }}
          >
            + Add Record
          </Button>
          )}
        </Stack>
        )}
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid item xs={12} md={8}>
          {/* Today's Appointments — the whole panel, because its content IS the refused read. */}
          {can.todaysAppointmentsTile && (
          <Paper sx={{ p: 3, borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={1}
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="h6" fontWeight={600}>
                  Today&apos;s Appointments
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  {stats.todaysAppointments} Total
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                <Chip
                  label={`${stats.fieldVisits} Barn Visits`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: 'divider' }}
                />
                <Chip
                  label={`${stats.inClinic} Clinic`}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: 'divider' }}
                />
              </Stack>
            </Box>

            {todaysAppointments.length === 0 ? (
              <Box py={4} textAlign="center">
                <Typography color="text.secondary">
                  No appointments scheduled for today
                </Typography>
              </Box>
            ) : (
              <Stack divider={<Divider />} spacing={0}>
                {todaysAppointments.map(appt => {
                  const statusStyle = getStatusChipStyle(appt.date);
                  const typeStyle = getTypeChipStyle(appt.type);
                  return (
                    <Box
                      key={appt.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1, sm: 2 },
                        py: 1.5,
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      }}
                    >
                      <Box sx={{ minWidth: 60, textAlign: 'center' }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            lineHeight: 1.2,
                            fontVariantNumeric: 'tabular-nums',
                          }}
                          data-testid="dashboard-appointment-time"
                        >
                          {appt.time}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.secondary', fontSize: '0.65rem', fontWeight: 500 }}
                          data-testid="dashboard-appointment-period"
                        >
                          {appt.period}
                        </Typography>
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 120 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {appt.horseName}{' '}
                          <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            fontWeight={400}
                          >
                            ({appt.ownerName})
                          </Typography>
                        </Typography>
                      </Box>

                      <Chip
                        label={appt.type}
                        size="small"
                        sx={{
                          bgcolor: typeStyle.bgcolor,
                          color: typeStyle.color,
                          fontWeight: 500,
                          fontSize: '0.75rem',
                        }}
                      />

                      {appt.location && (
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 80 }}>
                          <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 100 }}>
                            {appt.location}
                          </Typography>
                        </Box>
                      )}

                      {appt.vetName && (
                        <Box display="flex" alignItems="center" gap={0.5} sx={{ minWidth: 80 }}>
                          <PersonIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {appt.vetName}
                          </Typography>
                        </Box>
                      )}

                      <Chip
                        label={statusStyle.label}
                        size="small"
                        sx={{
                          bgcolor: statusStyle.bgcolor,
                          color: statusStyle.color,
                          fontWeight: 500,
                          fontSize: '0.7rem',
                          height: 24,
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            )}

            <Box mt={2}>
              {can.viewAllAppointments && (
                <Button
                  component={Link}
                  to="/appointments"
                  endIcon={<ChevronRightIcon />}
                  size="small"
                >
                  View all
                </Button>
              )}
            </Box>
          </Paper>
          )}

          {/* spacer */}
        </Grid>

        {/* Right Column */}
        <Grid item xs={12} md={4}>
          {/* Upcoming: derived from the SAME appointments query as the panel above. */}
          {can.todaysAppointmentsTile && (
          <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2 }}>
            <Typography variant="h6" fontWeight={600}>
              Upcoming appointments
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Next 7 days
            </Typography>

            <Box mt={3}>
              <Typography
                sx={{ fontSize: '2.5rem', fontWeight: 700, lineHeight: 1, color: '#3B2E1A' }}
              >
                {upcomingStats.total} <Typography component="span" sx={{ fontSize: '1rem', fontWeight: 400, color: 'text.secondary' }}>booked</Typography>
              </Typography>
            </Box>
          </Paper>
          )}

          {/* The whole card is a link into /billing, which <ScreenRoute> refuses without the same
              slug — so hiding the figure and leaving the link would just move the refusal. */}
          {can.unpaidInvoicesTile && (
          <Paper
            component={Link}
            to="/billing"
            sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none', mb: 2, textDecoration: 'none', display: 'block', cursor: 'pointer', '&:hover': { borderColor: 'primary.main' } }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6" fontWeight={600}>Unpaid Invoices</Typography>
              {(unpaid?.pastDueCount ?? 0) > 0 && (
                <Typography
                  variant="caption"
                  data-testid="dashboard-past-due-count"
                  sx={{ color: 'error.main', fontWeight: 600 }}
                >
                  {unpaid?.pastDueCount} Past due
                </Typography>
              )}
            </Box>
            <Stack direction="row" spacing={4} alignItems="baseline">
              <Box>
                <Typography variant="h4" fontWeight={700} sx={{ lineHeight: 1 }} data-testid="dashboard-open-invoice-count">
                  {stats.openInvoicesCount}
                </Typography>
                <Typography variant="caption" color="text.secondary">Open</Typography>
              </Box>
              <Box>
                <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ lineHeight: 1 }} data-testid="dashboard-unpaid-owed">
                  {/* `money.ts`, like every other money figure in the app
                      (`.claude/rules/frontend.md`). The raw `toLocaleString`
                      this replaced took the HOST's locale — a de-DE browser
                      printed "$1.234" for $1,234 — and rounded to whole
                      dollars, so $699.78 of AR read "$700". */}
                  {formatDollars(stats.unpaidInvoices)}
                </Typography>
                <Typography variant="caption" color="text.secondary">Total owed</Typography>
              </Box>
            </Stack>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                View all <ChevronRightIcon sx={{ fontSize: 18 }} />
              </Typography>
            </Box>
          </Paper>
          )}

          {/* Two INDEPENDENT figures in one card: patients is view:patients, active clients is
              view:clients, and the card itself disappears only when both are refused. */}
          {anyControl(can.patientsTile, can.activeClientsTile) && (
          <Paper sx={{ p: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
              Practice Overview
            </Typography>
            <Stack spacing={1.5}>
              {can.patientsTile && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Total patients</Typography>
                  <Typography variant="body2" fontWeight={600}>{stats.totalPatients}</Typography>
                </Box>
              )}
              {can.activeClientsTile && (
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Active clients</Typography>
                  <Typography variant="body2" fontWeight={600}>{stats.activeClients}</Typography>
                </Box>
              )}
            </Stack>
          </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
